/**
 * Surface bridge: what the user is looking at, as the `pageContext` Inky's server agent receives
 * every turn (CONTRACTS-W2 §C; ported from Cairn Flint lib/flint-bridge.ts).
 *
 * Generalizes the ActiveEditorContext snapshot (editorBridge.snapshotEditor): when an editor is
 * open the surface carries its outline, the selection, the pending review and the top-level
 * blocks (text and html, capped to a size budget so a long document never makes the request
 * huge); otherwise it names the project, requirements view or meeting on screen. The server
 * sanitizes all of it again; nothing here is trusted.
 */

import { snapshotEditor } from "./editorBridge";
import { aliasImageSources } from "./editHtml";

/** Size caps (the server applies the same ones). */
export const SURFACE_CAPS = Object.freeze({
  blocks: 400,
  blockText: 4000,
  blockHtml: 12000,
  totalHtml: 150000,
  outlineText: 300,
  selectionText: 4000,
  title: 200,
  blockFieldIds: 50,
});

/**
 * Cut a string.
 * @param {*} value - Raw value.
 * @param {number} max - Max characters.
 * @returns {string} String.
 */
const cut = (value, max) => {
  const text = typeof value === "string" ? value : value == null ? "" : String(value);
  return text.length > max ? text.slice(0, max) : text;
};

/** Template field ids in html (data-tpl-field / data-field-id), as the server reads them. */
const FIELD_ID_RE = /data-(?:tpl-field|field-id)\s*=\s*["']([A-Za-z0-9_-]+)["']/g;

/**
 * Template field ids inside a block's html.
 * @param {string} html - Block html.
 * @returns {string[]} Unique ids, in order (at most `SURFACE_CAPS.blockFieldIds`).
 */
export const fieldIdsInHtml = (html) => {
  const ids = new Set();
  for (const match of String(html || "").matchAll(FIELD_ID_RE)) ids.add(match[1]);
  return [...ids].slice(0, SURFACE_CAPS.blockFieldIds);
};

/**
 * Blocks for the request: text always (capped), html while the budget lasts. Pasted images are
 * sent as short stable aliases (`src="quill-img:..."`, see editHtml.js) instead of their base64
 * data, BEFORE the size is measured, so an image neither eats the html budget nor has to be
 * echoed byte for byte by the model; clientActions restores them from the live editor. A block
 * whose html still does not fit is marked `truncated`: doc_read says its html is unavailable
 * and the server refuses to rebuild it with replace_block.
 * Field ids always travel per block, apart from the html, so Inky can still fill (and must
 * still keep) the fields of a block whose html was dropped.
 * @param {Array<object>} blocks - Editor blocks.
 * @returns {Array<object>} Capped blocks.
 */
export const capBlocks = (blocks) => {
  let budget = SURFACE_CAPS.totalHtml;
  return (blocks || []).slice(0, SURFACE_CAPS.blocks).map((block) => {
    const html = aliasImageSources(typeof block.html === "string" ? block.html : "");
    const fits = html.length > 0 && html.length <= SURFACE_CAPS.blockHtml && html.length <= budget;
    if (fits) budget -= html.length;
    const fieldIds = fieldIdsInHtml(html);
    return {
      id: block.id,
      type: block.type,
      ...(block.level ? { level: block.level } : {}),
      text: cut(block.text, SURFACE_CAPS.blockText),
      ...(fits ? { html } : {}),
      ...(block.locked ? { locked: true } : {}),
      ...(!fits && html ? { truncated: true } : {}),
      ...(fieldIds.length ? { fieldIds } : {}),
    };
  });
};

/**
 * The surface for an active context (see projectGrounding.resolveActiveContext).
 * @param {{active: object, search?: string, getEditorRef?: Function}} params - Inputs.
 * @returns {object} Surface.
 */
export const buildSurface = ({ active, search = "", getEditorRef }) => {
  const kind = active?.kind || "general";
  const fallbackMeta =
    kind === "document"
      ? { kind: "document", documentId: active.document?.id || null, title: active.document?.name || "Document" }
      : kind === "template"
        ? { kind: "template", templateId: active.template?.id || null, title: active.template?.name || "Template" }
        : null;
  const editor = kind === "document" || kind === "template" ? snapshotEditor(getEditorRef, { fallbackMeta }) : null;

  if (editor) {
    const isTemplate = editor.meta?.kind === "template";
    const blocks = editor.bridged ? capBlocks(editor.blocks) : [];
    return {
      kind: isTemplate ? "template" : "document",
      id: (isTemplate ? editor.meta?.templateId : editor.meta?.documentId) || null,
      title: cut(editor.meta?.title || fallbackMeta?.title || "", SURFACE_CAPS.title),
      projectId: active.project?.id || null,
      outline: (editor.blocks || []).slice(0, SURFACE_CAPS.blocks).map((block) => ({
        id: block.id,
        type: block.type,
        ...(block.level ? { level: block.level } : {}),
        text: cut(block.text, SURFACE_CAPS.outlineText),
        ...(block.locked ? { locked: true } : {}),
      })),
      blocks,
      selection: editor.selection?.text
        ? { text: cut(editor.selection.text, SURFACE_CAPS.selectionText), blockIds: (editor.selection.blockIds || []).slice(0, 50) }
        : null,
      pendingReview: editor.pendingReview
        ? {
            reviewId: editor.pendingReview.reviewId || null,
            changeCount: editor.pendingReview.changes?.length || 0,
            label: cut(editor.pendingReview.label || "", 80),
          }
        : null,
      meta: {
        readOnly: Boolean(editor.meta?.readOnly),
        isEmpty: Boolean(editor.isEmpty),
        wordCount: editor.wordCount || 0,
        bridged: Boolean(editor.bridged),
      },
    };
  }

  if (kind === "document" || kind === "template") {
    // The editor route is open but the editor has not registered yet.
    return {
      kind,
      id: (kind === "document" ? active.document?.id : active.template?.id) || null,
      title: cut(fallbackMeta?.title || "", SURFACE_CAPS.title),
      projectId: active.project?.id || null,
      meta: { bridged: false },
    };
  }
  if (kind === "project" && active.project) {
    const tab = new URLSearchParams(search || "").get("tab");
    return { kind: tab === "requirements" ? "requirements" : "project", id: active.project.id, title: cut(active.project.name, SURFACE_CAPS.title), projectId: active.project.id };
  }
  if (kind === "session" && active.session) {
    return { kind: "session", id: active.session.id, title: cut(active.session.name, SURFACE_CAPS.title), projectId: active.project?.id || null };
  }
  return { kind: "none" };
};

/**
 * The full page context for one turn.
 * @param {{pathname: string, search: string, title?: string, active: object, getEditorRef?: Function}} params - Inputs.
 * @returns {{path: string, title: string, surface: object}} Page context.
 */
export const buildPageContext = ({ pathname, search, title, active, getEditorRef }) => ({
  path: cut(`${pathname || ""}${search || ""}`, 300),
  title: cut(title || (typeof document !== "undefined" ? document.title : ""), SURFACE_CAPS.title),
  surface: buildSurface({ active, search, getEditorRef }),
});

/**
 * Browser-applied tools this page can take (sent as `clientTools`).
 * @param {object} surface - Surface from {@link buildSurface}.
 * @returns {string[]} Tool names.
 */
export const clientToolsFor = (surface) => [
  "navigate",
  "open_settings",
  "stage_requirement_changes",
  "create_document_from_template",
  "create_customer",
  "create_project",
  "link_sessions",
  ...((surface?.kind === "document" || surface?.kind === "template") && surface?.meta?.bridged && !surface?.meta?.readOnly ? ["doc_edit_plan"] : []),
];
